import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  Lock,
  Mail,
  Eye,
  EyeOff,
  Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { setToken, setUser } from '../utils/auth';
import { toast } from 'sonner';
import api from '../utils/api';
import NavbarSimple from '../components/layout/NavbarSimple';

const UnifiedLoginPagePro = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isArabic = i18n.language === 'ar';
  const [selectedPortal, setSelectedPortal] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [credentials, setCredentials] = useState({
    email: '',
    password: '',
  });

  const text = {
    pageTitle: isArabic ? '\u0628\u0648\u0627\u0628\u0627\u062a \u0627\u0644\u062f\u062e\u0648\u0644' : 'Access Portals',
    pageSubtitle: isArabic
      ? '\u0627\u062e\u062a\u0631 \u0627\u0644\u0628\u0648\u0627\u0628\u0629 \u0627\u0644\u0645\u0646\u0627\u0633\u0628\u0629 \u0644\u0644\u0648\u0635\u0648\u0644 \u0625\u0644\u0649 \u062e\u062f\u0645\u0627\u062a \u0648\u0627\u0632\u0646 \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064a\u0629 \u0648\u0625\u062f\u0627\u0631\u0629 \u0645\u0644\u0641\u0627\u062a\u0643 \u0628\u0627\u062d\u062a\u0631\u0627\u0641\u064a\u0629.'
      : 'Choose the right portal to access Wazin legal services and manage your files professionally.',
    trustedBadge: isArabic ? '\u062f\u062e\u0648\u0644 \u0645\u0646\u0638\u0645 \u0648\u0622\u0645\u0646' : 'Organized Secure Access',
    loginTitle: isArabic ? '\u062a\u0633\u062c\u064a\u0644 \u0627\u0644\u062f\u062e\u0648\u0644' : 'Sign In',
    email: isArabic ? '\u0627\u0644\u0628\u0631\u064a\u062f \u0627\u0644\u0625\u0644\u0643\u062a\u0631\u0648\u0646\u064a' : 'Email',
    emailPlaceholder: isArabic ? '\u0623\u062f\u062e\u0644 \u0628\u0631\u064a\u062f\u0643 \u0627\u0644\u0625\u0644\u0643\u062a\u0631\u0648\u0646\u064a' : 'Enter your email',
    password: isArabic ? '\u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631' : 'Password',
    passwordPlaceholder: isArabic ? '\u0623\u062f\u062e\u0644 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631' : 'Enter your password',
    submit: isArabic ? '\u0627\u0644\u062f\u062e\u0648\u0644 \u0625\u0644\u0649 \u0627\u0644\u0628\u0648\u0627\u0628\u0629' : 'Access Portal',
    submitting: isArabic ? '\u062c\u0627\u0631\u064d \u062a\u0633\u062c\u064a\u0644 \u0627\u0644\u062f\u062e\u0648\u0644...' : 'Signing in...',
    registerPrompt: isArabic ? '\u0644\u064a\u0633 \u0644\u062f\u064a\u0643 \u062d\u0633\u0627\u0628\u061f' : "Don't have an account?",
    registerAction: isArabic ? '\u0623\u0646\u0634\u0626 \u062d\u0633\u0627\u0628\u0643' : 'Create an account',
    loginSuccess: isArabic ? '\u062a\u0645 \u062a\u0633\u062c\u064a\u0644 \u0627\u0644\u062f\u062e\u0648\u0644 \u0628\u0646\u062c\u0627\u062d' : 'Login successful',
    invalidPortal: isArabic
      ? '\u0647\u0630\u0627 \u0627\u0644\u062d\u0633\u0627\u0628 \u063a\u064a\u0631 \u0645\u062e\u0635\u0635 \u0644\u0644\u062f\u062e\u0648\u0644 \u0625\u0644\u0649 \u0647\u0630\u0647 \u0627\u0644\u0628\u0648\u0627\u0628\u0629'
      : 'This account is not authorized for this portal',
    loginFailed: isArabic ? '\u062a\u0639\u0630\u0631 \u062a\u0633\u062c\u064a\u0644 \u0627\u0644\u062f\u062e\u0648\u0644' : 'Login failed',
    cardAction: isArabic ? '\u062a\u0633\u062c\u064a\u0644 \u0627\u0644\u062f\u062e\u0648\u0644' : 'Sign In',
    supportTitle: isArabic ? '\u0648\u0635\u0648\u0644 \u0648\u0627\u0636\u062d \u0644\u0643\u0644 \u062c\u0647\u0629' : 'Clear Access for Every Role',
    supportText: isArabic
      ? '\u062a\u0645 \u062a\u0646\u0638\u064a\u0645 \u0627\u0644\u0628\u0648\u0627\u0628\u0627\u062a \u0644\u062a\u0646\u0627\u0633\u0628 \u0627\u0644\u0623\u0641\u0631\u0627\u062f \u0648\u0642\u0637\u0627\u0639 \u0627\u0644\u0623\u0639\u0645\u0627\u0644 \u0648\u0627\u0644\u0645\u0648\u0638\u0641\u064a\u0646 \u0648\u0627\u0644\u0625\u062f\u0627\u0631\u0629\u060c \u0645\u0639 \u062a\u062c\u0631\u0628\u0629 \u0645\u0648\u062d\u062f\u0629 \u062a\u062d\u0627\u0641\u0638 \u0639\u0644\u0649 \u0627\u0644\u0648\u0636\u0648\u062d \u0648\u0627\u0644\u0633\u0631\u064a\u0629.'
      : 'Portals are organized for individuals, businesses, staff, and administration in one clear and secure experience.',
    supportHint: isArabic
      ? '\u0643\u0644 \u0628\u0648\u0627\u0628\u0629 \u0645\u0647\u064a\u0623\u0629 \u062d\u0633\u0628 \u0637\u0628\u064a\u0639\u0629 \u0627\u0644\u0639\u0645\u0644 \u0648\u0627\u0644\u0635\u0644\u0627\u062d\u064a\u0627\u062a.'
      : 'Each portal is tailored to its role and access level.',
    rightsLine: isArabic
      ? '\u062c\u0645\u064a\u0639 \u0627\u0644\u062d\u0642\u0648\u0642 \u0645\u062d\u0641\u0648\u0638\u0629 \u0644\u062f\u0649 \u0645\u0646\u0635\u0629 \u0648\u0627\u0632\u0646 \u00a9 2026'
      : 'All rights reserved to Wazin Platform © 2026',
  };

  const portals = [
    {
      id: 'individuals',
      title: isArabic ? '\u0628\u0648\u0627\u0628\u0629 \u0627\u0644\u0623\u0641\u0631\u0627\u062f' : 'Individuals Portal',
      badgeLabel: isArabic ? '\u0623\u0641\u0631\u0627\u062f' : 'Individuals',
      subtitle: isArabic ? '\u0644\u0644\u0639\u0645\u0644\u0627\u0621 \u0645\u0646 \u0627\u0644\u0623\u0641\u0631\u0627\u062f' : 'For individual clients',
      description: isArabic
        ? '\u0645\u062a\u0627\u0628\u0639\u0629 \u0627\u0644\u0642\u0636\u0627\u064a\u0627 \u0648\u0627\u0644\u0639\u0642\u0648\u062f \u0648\u0627\u0644\u0627\u0633\u062a\u0634\u0627\u0631\u0627\u062a \u0648\u0627\u0644\u0637\u0644\u0628\u0627\u062a \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064a\u0629 \u0645\u0646 \u0645\u0643\u0627\u0646 \u0648\u0627\u062d\u062f.'
        : 'Track cases, contracts, consultations, and legal requests in one place.',
      accent: '#2E8B57',
      role: 'client',
      tone: 'from-[#2E8B57] to-[#1F6B42]',
      logoFilter:
        'brightness(0) saturate(100%) invert(38%) sepia(40%) saturate(730%) hue-rotate(97deg) brightness(93%) contrast(91%)',
    },
    {
      id: 'business',
      title: isArabic ? '\u0628\u0648\u0627\u0628\u0629 \u0627\u0644\u0623\u0639\u0645\u0627\u0644' : 'Business Portal',
      badgeLabel: isArabic ? '\u0623\u0639\u0645\u0627\u0644' : 'Business',
      subtitle: isArabic ? '\u0644\u0644\u0634\u0631\u0643\u0627\u062a \u0648\u0627\u0644\u0645\u0646\u0634\u0622\u062a' : 'For companies and enterprises',
      description: isArabic
        ? '\u0625\u062f\u0627\u0631\u0629 \u0627\u0644\u0645\u0644\u0641\u0627\u062a \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064a\u0629 \u0627\u0644\u0645\u0624\u0633\u0633\u064a\u0629 \u0648\u0627\u0644\u0639\u0642\u0648\u062f \u0648\u0627\u0644\u062a\u0630\u0627\u0643\u0631 \u0645\u0639 \u0631\u0624\u064a\u0629 \u0623\u0648\u0636\u062d \u0644\u0644\u0623\u0639\u0645\u0627\u0644.'
        : 'Manage legal business matters, contracts, and tickets with a clearer enterprise view.',
      accent: '#1F5FAE',
      role: 'client',
      tone: 'from-[#1F5FAE] to-[#184C8C]',
      logoFilter:
        'brightness(0) saturate(100%) invert(27%) sepia(70%) saturate(1696%) hue-rotate(191deg) brightness(92%) contrast(93%)',
    },
    {
      id: 'employees',
      title: isArabic ? '\u0628\u0648\u0627\u0628\u0629 \u0627\u0644\u0645\u0648\u0638\u0641\u064a\u0646' : 'Staff Portal',
      badgeLabel: isArabic ? '\u0627\u0644\u0645\u0648\u0638\u0641\u0648\u0646' : 'Staff',
      subtitle: isArabic ? '\u0644\u0644\u0645\u0648\u0638\u0641\u064a\u0646 \u0648\u0627\u0644\u0645\u062d\u0627\u0645\u064a\u0646' : 'For staff and lawyers',
      description: isArabic
        ? '\u0648\u0635\u0648\u0644 \u0645\u0646\u0638\u0645 \u0625\u0644\u0649 \u0627\u0644\u0642\u0636\u0627\u064a\u0627 \u0648\u0627\u0644\u0645\u0647\u0627\u0645 \u0648\u0627\u0644\u0648\u062b\u0627\u0626\u0642 \u0648\u0633\u064a\u0631 \u0627\u0644\u0639\u0645\u0644 \u0627\u0644\u062f\u0627\u062e\u0644\u064a.'
        : 'Structured access to cases, tasks, documents, and internal workflow.',
      accent: '#6B7280',
      role: 'lawyer',
      tone: 'from-[#6B7280] to-[#4B5563]',
      logoFilter:
        'brightness(0) saturate(100%) invert(44%) sepia(8%) saturate(457%) hue-rotate(182deg) brightness(92%) contrast(88%)',
    },
    {
      id: 'admin',
      title: isArabic ? '\u0628\u0648\u0627\u0628\u0629 \u0627\u0644\u0625\u062f\u0627\u0631\u0629' : 'Administration Portal',
      badgeLabel: isArabic ? '\u0627\u0644\u0625\u062f\u0627\u0631\u0629' : 'Admin',
      subtitle: isArabic ? '\u0644\u0644\u0625\u062f\u0627\u0631\u0629 \u0627\u0644\u0639\u0644\u064a\u0627' : 'For senior management',
      description: isArabic
        ? '\u0644\u0648\u062d\u0629 \u062a\u0634\u063a\u064a\u0644 \u0648\u0625\u062f\u0627\u0631\u0629 \u0648\u0645\u062a\u0627\u0628\u0639\u0629 \u0644\u0644\u0639\u0645\u0644\u064a\u0627\u062a \u0627\u0644\u062f\u0627\u062e\u0644\u064a\u0629 \u0648\u0627\u0644\u0645\u0624\u0634\u0631\u0627\u062a \u0627\u0644\u0631\u0626\u064a\u0633\u064a\u0629.'
        : 'An operating and management view for internal operations and key indicators.',
      accent: '#A27849',
      role: 'admin',
      tone: 'from-[#A27849] to-[#8F6A3F]',
      logoFilter:
        'brightness(0) saturate(100%) invert(49%) sepia(21%) saturate(1030%) hue-rotate(352deg) brightness(92%) contrast(86%)',
    },
  ];

  const handlePortalClick = (portal) => {
    setSelectedPortal(portal);
    setShowLoginModal(true);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/login', credentials);
      const { token, user } = response.data;

      if (user.role !== selectedPortal.role) {
        toast.error(text.invalidPortal);
        setLoading(false);
        return;
      }

      setToken(token);
      setUser(user);
      toast.success(text.loginSuccess);

      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'lawyer') {
        if (user.user_type === 'staff') {
          navigate('/admin/labor-intake');
        } else {
          navigate('/lawyer');
        }
      } else if (user.role === 'client') {
        if (
          selectedPortal.id === 'business' ||
          user.client_type === 'business' ||
          user.user_type === 'business'
        ) {
          navigate('/business/dashboard');
        } else {
          navigate('/client/dashboard');
        }
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || text.loginFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = () => {
    setShowLoginModal(false);
    navigate('/register');
  };

  return (
    <div className="min-h-screen bg-[#F8F7F3] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(circle_at_top,_rgba(31,95,174,0.11),_transparent_62%)]" />
        <div className="absolute -top-12 left-1/2 h-[620px] w-[620px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,_rgba(162,120,73,0.07),_transparent_72%)]" />
      </div>

      <div className="relative z-20">
        <NavbarSimple />
      </div>

      <main className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-36 pb-16 md:pb-24">
        <section className="mb-10 md:mb-14">
          <div className="mx-auto max-w-4xl">
            <div className={`${isArabic ? 'text-right' : 'text-left'} text-center`}>
              <h1 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-[#1F3B64] leading-[1.15]">
                {text.pageTitle}
              </h1>

              <p className="mt-4 max-w-3xl text-lg md:text-xl leading-8 text-slate-600">
                {text.pageSubtitle}
              </p>

            </div>
          </div>
        </section>

        <section className="relative">
          <div className="pointer-events-none absolute inset-x-0 top-10 flex justify-center">
            <img
              src="/images/wazin-mark.png"
              alt=""
              aria-hidden="true"
              className="h-[620px] w-[300px] object-contain opacity-[0.06]"
            />
          </div>

          <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {portals.map((portal, index) => (
              <button
                key={portal.id}
                type="button"
                onClick={() => handlePortalClick(portal)}
                className="group relative text-inherit text-start rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_14px_34px_rgba(18,35,62,0.08)] transition-all duration-300 hover:-translate-y-3 hover:shadow-[0_28px_56px_rgba(18,35,62,0.14)]"
                style={{ animation: `portalFade .45s ease-out ${index * 0.08}s both` }}
              >
                <div
                  className={`absolute inset-x-0 top-0 h-1.5 rounded-t-[28px] bg-gradient-to-r ${portal.tone}`}
                />

                <div className="flex flex-col items-center gap-4 pt-1">
                  <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-[1.75rem] bg-[#FAF8F3] border border-slate-200 transition-all duration-300 group-hover:-translate-y-1 group-hover:scale-110">
                    <img
                      src="/images/wazin-mark.png"
                      alt="Wazin icon"
                      className="h-16 w-16 object-contain"
                      style={{ filter: portal.logoFilter }}
                    />
                  </div>

                  <div
                    className="inline-flex h-10 min-w-[72px] items-center justify-center rounded-full px-3 text-[12px] font-bold text-white shadow-sm transition-all duration-300 group-hover:-translate-y-1"
                    style={{ backgroundColor: portal.accent }}
                  >
                    {portal.badgeLabel}
                  </div>
                </div>

                <div className="mt-5 text-center">
                  <h3 className="text-[1.45rem] font-bold text-[#1F3B64] leading-tight">
                    {portal.title}
                  </h3>
                  <p className="mt-2 text-sm font-semibold" style={{ color: portal.accent }}>
                    {portal.subtitle}
                  </p>
                  <p className="mt-4 min-h-[72px] text-[15px] leading-7 text-slate-600">
                    {portal.description}
                  </p>
                </div>

                <div className="mt-6 border-t border-slate-100 pt-5">
                  <span
                    className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl text-sm font-bold text-white transition-all duration-300 group-hover:-translate-y-1 shadow-sm"
                    style={{ backgroundColor: portal.accent }}
                  >
                    <span>{text.cardAction}</span>
                    <ArrowLeft className={`h-4 w-4 ${isArabic ? 'rotate-180' : ''}`} />
                  </span>
                </div>
              </button>
            ))}
          </div>

          <p className="mt-8 text-center text-sm font-medium text-slate-400">
            {text.rightsLine}
          </p>
          <div className="mt-3 flex justify-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#22C55E]/20 bg-[#F0FDF4] px-4 py-2 text-sm font-semibold text-[#15803D] shadow-sm">
              <Sparkles className="h-4 w-4 text-[#16A34A]" />
              {text.trustedBadge}
            </span>
          </div>
        </section>
      </main>

      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="sm:max-w-[460px] rounded-[24px] border border-[#A27849]/20 bg-white p-0 shadow-[0_22px_55px_rgba(18,35,62,0.18)] overflow-hidden">
          {selectedPortal && (
            <>
              <div
                className="h-2 w-full"
                style={{ backgroundColor: selectedPortal.accent }}
              />
              <div className="p-6 md:p-7">
                <DialogHeader className="mb-2">
                  <DialogTitle>
                    <div className="flex flex-col items-center gap-4 text-center">
                      <div className="flex items-center gap-3">
                        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#FAF8F3] border border-slate-200">
                          <img
                            src="/images/wazin-mark.png"
                            alt="Wazin icon"
                            className="h-10 w-10 object-contain"
                            style={{ filter: selectedPortal.logoFilter }}
                          />
                        </div>
                        <div className={isArabic ? 'text-right' : 'text-left'}>
                          <div className="text-lg font-bold text-[#1F3B64]">
                            {selectedPortal.badgeLabel}
                          </div>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-[#1F3B64]">
                          {text.loginTitle}
                        </h3>
                        <p className="mt-1 text-sm font-semibold" style={{ color: selectedPortal.accent }}>
                          {selectedPortal.title}
                        </p>
                      </div>
                    </div>
                  </DialogTitle>
                </DialogHeader>

                <form onSubmit={handleLogin} className="mt-6 space-y-5">
                  <div>
                    <Label htmlFor="email" className="mb-2 block text-sm font-semibold text-slate-700">
                      {text.email}
                    </Label>
                    <div className="relative">
                      <Mail className={`absolute top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400 ${isArabic ? 'right-3' : 'left-3'}`} />
                      <Input
                        id="email"
                        type="email"
                        dir={isArabic ? 'rtl' : 'ltr'}
                        value={credentials.email}
                        onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                        className={`${isArabic ? 'pr-11 pl-4 text-right' : 'pl-11 pr-4 text-left'} h-12 rounded-xl border-slate-200 bg-[#FAFAF7] text-slate-900 focus-visible:ring-[#A27849]`}
                        placeholder={text.emailPlaceholder}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="password" className="mb-2 block text-sm font-semibold text-slate-700">
                      {text.password}
                    </Label>
                    <div className="relative">
                      <Lock className={`absolute top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400 ${isArabic ? 'right-3' : 'left-3'}`} />
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        dir={isArabic ? 'rtl' : 'ltr'}
                        value={credentials.password}
                        onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                        className={`${isArabic ? 'pr-11 pl-11 text-right' : 'pl-11 pr-11 text-left'} h-12 rounded-xl border-slate-200 bg-[#FAFAF7] text-slate-900 focus-visible:ring-[#A27849]`}
                        placeholder={text.passwordPlaceholder}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className={`absolute top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-[#A27849] ${isArabic ? 'left-3' : 'right-3'}`}
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="h-12 w-full rounded-xl text-base font-bold text-white"
                    style={{ backgroundColor: selectedPortal.accent }}
                  >
                    {loading ? text.submitting : text.submit}
                  </Button>

                  <div className="border-t border-slate-200 pt-4 text-center text-sm text-slate-600">
                    <span>{text.registerPrompt}</span>{' '}
                    <button
                      type="button"
                      onClick={handleRegister}
                      className="font-bold text-[#A27849] transition-colors hover:text-[#1F3B64]"
                    >
                      {text.registerAction}
                    </button>
                  </div>
                </form>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <style jsx>{`
        @keyframes portalFade {
          from {
            opacity: 0;
            transform: translateY(28px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default UnifiedLoginPagePro;
